import ObserverCommon from './ObserverCommon';
import BuildVersion from './BuildVersion';

import {
  ECondition,
  EMatch,
  EOperator,
  IEvent,
  IExternalID,
  IField,
  IFieldDefinition,
  IEventForm,
  IIdentifier,
  ILocation,
  INodeProps,
  ITarget,
  IValidation,
  TEventType,
  IAttributes,
  EAlertType,
  IPosition,
  ISelectors,
  IFieldAlert,
  IElementID,
  IDomElement,
  IXPath,
  IEventParams,
  ILabel,
  ILogLevels,
  ICapturedEvents
} from './ObjectTypes';

/**
 * Observes events and fires a custom event with a wrapper on the original event
 */
export default class EventObserver extends ObserverCommon {
  version: string;
  name_attr: string;
  id_attr: string;
  form_attrs: string[];
  current_forms: IEventForm[];
  field_definitions: IFieldDefinition[];
  input_types: string[];
  markup_tags: string[];
  list_containers: string[];
  list_tags: string[];
  field_containers: { [key: string]: string[] };
  meta_keys: string[];
  click_inputs: string[];
  no_set_inputs: string[];
  cell_roles: string[];
  table_tags: string[];
  text_label_roles: string[];
  excluded_selectors: string[];
  windowDialog: IField | null;
  okDialogButton: IField | null;
  cancelDialogButton: IField | null;
  start_time: number;
  ctrl_key: boolean;
  shift_key: boolean;
  alt_key: boolean;
  last_event: IEvent | undefined;
  logging: boolean;
  enabled: boolean;
  observer?: MutationObserver;
  observerEvent?: string;
  ignore_event: boolean;
  changeTimerId?: number;
  logLevels: ILogLevels;
  capturedEvents: ICapturedEvents;

  /**
   * Creates a new instance.
   * @constructor
   */
  constructor() {
    super();

    this.version = BuildVersion;
    this.enabled = false;
    this.logging = false;
    this.capturedEvents = {
      click: true,
      input: true,
      keys: true,
      dialog: true
    };
    this.ignore_event = false;
    this.observerEvent = 'observedEvent';
    this.name_attr = 'data-form-name';
    this.id_attr = 'data-form-id';
    this.form_attrs = ['data-form-id', 'data-form-name'];
    this.field_definitions = [
      {
        name: 'P-TREENODE',
        type: 'tag',
        role: 'treeitem',
        parent: 'P-TREE',
        clickable: true,
        label: 'P-TREE'
      },
      {
        name: 'P-TREE',
        type: 'tag',
        role: 'tree',
        label: 'P-TREE'
      },

      {
        name: 'P-DROPDOWNITEM',
        type: 'tag',
        role: 'dropdownitem',
        parent: 'P-DROPDOWN',
        label: 'APP-COMBO',
        clickable: true,
        action: 'Select'
      },
      {
        name: 'P-DROPDOWN',
        type: 'tag',
        role: 'dropdown',
        parent: 'APP-COMBO',
        label: 'APP-COMBO',
        action: 'Select'
      },
      {
        name: 'APP-COMBO',
        type: 'tag',
        role: 'combo',
        action: 'Select'
      },

      {
        name: 'P-ACCORDIONTAB',
        type: 'tag',
        role: 'tab',
        parent: 'P-ACCORDION'
      },
      {
        name: 'P-ACCORDION',
        type: 'tag',
        role: 'accordion'
      },

      {
        name: 'P-RADIOBUTTON',
        type: 'tag',
        role: 'radiobutton',
        parent: 'APP-RADIO',
        clickable: true
      },
      {
        name: 'APP-RADIO',
        type: 'tag',
        role: 'radiogroup'
      },

      {
        name: 'P-CHECKBOX',
        type: 'tag',
        role: 'checkbox',
        parent: 'APP-CHECKBOX',
        clickable: true
      },
      {
        name: 'APP-CHECKBOX',
        type: 'tag',
        role: 'checkbox'
      }
    ];
    this.input_types = ['select', 'input', 'textarea'];
    this.markup_tags = ['B', 'I', 'EM', 'STRONG', 'SUB', 'SPAN'];
    this.list_containers = ['select', 'dropdown', 'dropdownlist', 'list', 'tree', 'treelist', 'nav'];
    this.list_tags = ['SELECT', 'UL', 'OL'];
    this.field_containers = {
      item: ['dropdownlist', 'dropdown', 'listbox', 'tree', 'menu', 'list'],
      listitem: ['dropdownlist', 'dropdown', 'listbox', 'list'],
      option: ['dropdownlist', 'dropdown', 'listbox'],
      checklistitem: ['checklist'],
      treeitem: ['tree'],
      text: ['page', 'panel', 'widget']
    };
    this.meta_keys = ['Control', 'Shift', 'Alt', 'Meta'];
    this.click_inputs = ['button', 'submit', 'reset', 'image'];
    this.no_set_inputs = ['radio', 'checkbox', ...this.click_inputs];
    this.cell_roles = ['cell', 'gridcell', 'rowheader'];
    this.table_tags = ['table', 'thead', 'tbody', 'tr', 'th', 'td'];
    this.text_label_roles = ['tab', 'button', 'link', 'label', 'item', 'listitem', 'checklistitem', 'menuitem'];

    this.excluded_selectors = ['form'];
    this.windowDialog = null;
    this.okDialogButton = null;
    this.cancelDialogButton = null;
    this.start_time = 0;
    this.ctrl_key = false;
    this.shift_key = false;
    this.alt_key = false;
    this.last_event = undefined;
    this.change = this.change.bind(this);
    this.click = this.click.bind(this);
    this.keydown = this.keydown.bind(this);
    this.keyup = this.keyup.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
    this.current_forms = this.getAllForms();
    this.observer = new MutationObserver(this.handleMutations);
    this.logLevels = {
      error: true,
      warn: false,
      info: false,
      verbose: false
    };
  }

  /**
   * Toggle recording events
   */
  observe(state: boolean, levels?: ILogLevels, captures?: ICapturedEvents) {
    if (levels) {
      this.logLevels = levels;
    }

    if (captures) {
      this.capturedEvents = captures;
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'Event Observer', EAlertType.info, this.logLevels, { enabled: state });

    this.enabled = state;

    if (this.enabled === true) {
      if (!this.start_time) {
        this.start_time = Date.now();
      }

      if (this.capturedEvents.click) {
        document.addEventListener('mouseup', this.click, true);
      }
      if (this.capturedEvents.keys) {
        document.addEventListener('keydown', this.keydown, true);
        document.addEventListener('keyup', this.keyup, true);
      }
      if (this.capturedEvents.input) {
        document.addEventListener('input', this.change, true);
      }

      this.initPollyFills();

      if (this.capturedEvents.dialog) {
        this.initWindowDialogs();
      }

      this.observer?.observe(document.body, {
        childList: true, // Children
        subtree: true // Grandchildren
      });
    } else {
      this.observer?.disconnect();

      if (this.capturedEvents.click) {
        document.removeEventListener('mouseup', this.click, true);
      }
      if (this.capturedEvents.keys) {
        document.removeEventListener('keydown', this.keydown, true);
        document.removeEventListener('keyup', this.keyup, true);
      }
      if (this.capturedEvents.input) {
        document.removeEventListener('input', this.change, true);
      }
    }
  }

  /**
   * Add polyfills for IE11
   */
  initPollyFills() {
    try {
      // test for scope support
      document.querySelector(':scope *');
    } catch (error) {
      (function (ElementPrototype) {
        // scope regex
        const scope = /:scope(?![\w-])/gi;

        // polyfill Element#querySelector
        const querySelectorWithScope = polyfill(ElementPrototype.querySelector);
        ElementPrototype.querySelector = function querySelector() {
          return querySelectorWithScope.apply(this, arguments); // eslint-disable-line
        };

        // polyfill Element#querySelectorAll
        const querySelectorAllWithScope = polyfill(ElementPrototype.querySelectorAll);
        ElementPrototype.querySelectorAll = function querySelectorAll() {
          return querySelectorAllWithScope.apply(this, arguments); // eslint-disable-line
        };

        // polyfill Element#matches
        if (ElementPrototype.matches) {
          const matchesWithScope = polyfill(ElementPrototype.matches);
          ElementPrototype.matches = function matches() {
            return matchesWithScope.apply(this, arguments); // eslint-disable-line
          };
        }

        // polyfill Element#closest
        if (ElementPrototype.closest) {
          const closestWithScope = polyfill(ElementPrototype.closest);
          ElementPrototype.closest = function closest() {
            return closestWithScope.apply(this, arguments); // eslint-disable-line
          };
        }

        /**
         *
         */
        function polyfill(qsa: any): any {
          return function (selectors: any): any {
            const hasScope = selectors && scope.test(selectors);

            if (hasScope) {
              const attr = `q${Math.floor(Math.random() * 9_000_000)}${1_000_000}`;

              arguments[0] = selectors.replace(scope, `[${attr}]`); // eslint-disable-line

              this.setAttribute(attr, ''); // eslint-disable-line

              const elementOrNodeList = qsa.apply(this, arguments); // eslint-disable-line

              this.removeAttribute(attr); // eslint-disable-line

              return elementOrNodeList;
            }

            return qsa.apply(this, arguments); // eslint-disable-line
          };
        }
      })(Element.prototype);
    }
  }

  /**
   * Handles change events on input elements
   */
  change(e: any) {
    const domEl = this.getEventTarget(e);
    if (!domEl.node || this.ignoreEvent(domEl.node)) {
      return;
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'change', EAlertType.info, this.logLevels, { e, domEl });

    if (this.isExcludedElement(domEl.node)) {
      return;
    }

    if (domEl.node.isContentEditable || domEl.node.tagName === 'TEXTAREA' || (domEl.node.tagName === 'INPUT' && !this.no_set_inputs.includes(domEl.node.type))) {
      this.debounceChange(this.buildChangeEvent, 200, domEl, e.isTrusted);
    } else {
      this.buildChangeEvent(domEl, e.isTrusted);
    }
  }

  /**
   *
   */
  debounceChange(method: (domEl: IDomElement, trusted: boolean) => void, delay: number, domEl: IDomElement, trusted: boolean) {
    clearTimeout(this.changeTimerId);
    this.changeTimerId = window.setTimeout(method.bind(this, domEl, trusted), delay);
  }

  /**
   *
   */
  buildChangeEvent(domEl: IDomElement, trusted: boolean) {
    const fields = this.getLineageFields(domEl);
    if (!fields.length) {
      return;
    }

    const target = this.getTargetField(fields);

    if (!target || !target.field) {
      return;
    }

    if (!ObserverCommon.change_roles.includes(target.field.type ?? '')) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'change', EAlertType.verbose, this.logLevels, { message: 'Target not valid for change', target });
      return;
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: target.form || this.parseForm(target.element),
      event: this.getChangeEvent(target.element, fields),
      fid: target.field.fid,
      keys: {
        ctrl: this.ctrl_key,
        alt: this.alt_key,
        shift: this.shift_key
      },
      trusted,
      fields
    };

    this.pushEvent(new_event, target.field, target.element);
  }

  /**
   *
   */
  click(e: any) {
    const button = e.button;
    if (button !== 0) {
      return;
    }

    const domEl: any = this.getEventTarget(e);
    const el = domEl.node;
    if (!el || this.ignoreEvent(el) || el.isContentEditable) {
      return;
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'click', EAlertType.info, this.logLevels, { e, el });

    if (this.isExcludedElement(el)) {
      return;
    }

    if (
      el.nodeName === 'SELECT' ||
      el.nodeName === 'TEXTAREA' ||
      el.nodeName === 'OPTION' ||
      (el.nodeName === 'INPUT' && !this.click_inputs.includes(el.type as string))
    ) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'click', EAlertType.info, this.logLevels, { msg: 'Ignore clicks on inputs' });
      return;
    }

    let _labelTarget: any;
    if (el.nodeName === 'LABEL') {
      _labelTarget = this.getLabelTarget(el);
      if (_labelTarget) {
        if (el.hasAttribute('aria-expanded') || this.hasValidClickRole(el)) {
          this.ignore_event = true;
        } else {
          return;
        }
      }
    }

    const uid = Math.random().toString(36).slice(2);
    let fields = this.getLineageFields(domEl);
    if (!fields.length) {
      return;
    }

    const target = this.getClickTargetField(fields, 'Click');

    if (!target || !target.field) {
      return;
    }

    if (target.field.type && ObserverCommon.click_roles.includes(target.field.type)) {
      const regionId = this.getValidAttributeValue(target.element, 'aria-controls');
      if (regionId) {
        const regionEl = document.getElementById(regionId);
        if (regionEl && ObserverCommon.expand_roles.includes(this.getNodeRole(regionEl))) {
          const toggleXpath = target.field.selectors.xpath;
          const toggleCss = target.field.selectors.css;

          let expanded = _labelTarget
            ? this.getValidPropertyValue(_labelTarget, 'checked')
            : this.getValidPropertyValue(target.element, 'expanded');

          if (!expanded) {
            expanded = ObserverCommon.isDisplayed(regionEl).toString();
          }

          fields = this.getLineageFields({ node: regionEl, path: this.getParents(regionEl).parents });
          if (fields.length) {
            target.field = fields[fields.length - 1];
            target.field.selectors.xpath2 = target.field.selectors.xpath;
            target.field.selectors.xpath2_source = target.field.selectors.xpath_source;
            target.field.selectors.css2 = target.field.selectors.css;
            target.field.selectors.xpath = toggleXpath;
            target.field.selectors.css = toggleCss;
            target.element = target.field.element;
            target.action = expanded === 'true'
              ? 'Collapse'
              : 'Expand';
          }
        }
      }
    }

    const new_event: IEvent = {
      time: Date.now(),
      url: window.location.href,
      form: target.form || this.parseForm(target.element),
      event: target.action,
      trusted: e.isTrusted,
      fid: target.field.fid,
      keys: {
        ctrl: this.ctrl_key,
        alt: this.alt_key,
        shift: this.shift_key
      },
      uid,
      fields
    };

    this.pushEvent(new_event, target.field, target.element);
  }

  /**
   *
   */
  keydown(e: any) {
    if (this.logging && this.logLevels.verbose) {
      console.group('keydown');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { e });

    const domEl: any = this.getEventTarget(e);
    const el = domEl.node;
    if (!el) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'No element' });

      if (this.logging) {
        console.groupEnd();
      }

      return;
    }

    if (this.ignoreEvent(el)) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'Ignored element', el });

      if (this.logging) {
        console.groupEnd();
      }

      return;
    }

    if (this.isExcludedElement(el)) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'Excluded element', el });

      if (this.logging) {
        console.groupEnd();
      }

      return;
    }

    const _key: string = e.key && !this.meta_keys.includes(e.key as string)
      ? e.key
      : '';
    const _pressKey = _key && ObserverCommon.press_keys.includes(_key.toUpperCase());

    if (!_pressKey) {
      if (this.input_types.includes(el.nodeName.toLowerCase())) {
        ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'Input element and no control key', el });

        if (this.logging) {
          console.groupEnd();
        }

        return;
      }

      if (el.isContentEditable) {
        ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'Content editable element and no control key', el });

        if (this.logging) {
          console.groupEnd();
        }

        return;
      }
    }

    if (e.ctrlKey) {
      this.ctrl_key = true;
    }

    if (e.shiftKey) {
      this.shift_key = true;
    }

    if (e.altKey) {
      this.alt_key = true;
    }

    if (!_key) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'No valid control key' });
      return;
    }

    const _fields: IField[] = this.getLineageFields(domEl);
    if (!_fields.length) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'KeyDown', EAlertType.verbose, this.logLevels, { msg: 'No field lineage', el });

      if (this.logging) {
        console.groupEnd();
      }

      return;
    }

    const _sField: IField = _fields[_fields.length - 1];
    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: this.parseForm(el),
      event: _pressKey ? 'SendKey' : 'Type',
      trusted: e.isTrusted,
      fields: _fields,
      fid: _sField.fid,
      key: _key,
      keys: {
        ctrl: this.ctrl_key,
        alt: this.alt_key,
        shift: this.shift_key
      }
    };

    if (this.logging) {
      console.groupEnd();
    }

    this.pushEvent(new_event, _sField, _sField.element);
  }

  /**
   *
   */
  keyup(e: any) {
    const el = document.activeElement;

    if (
      !el ||
      !this.meta_keys.includes(e.key) ||
      this.ignoreEvent(el) ||
      this.isExcludedElement(el)
    ) {
      return;
    }

    switch (e.key) {
      case 'Control':
        this.ctrl_key = false;
        break;
      case 'Shift':
        this.shift_key = false;
        break;
      case 'Alt':
        this.alt_key = false;
        break;
      default:
        break;
    }
  }

  /**
   * Processes mutations from the MutationObserver
   */
  handleMutations() {
    const allForms = this.getAllForms();
    const diffForms = allForms.filter((f2) => !this.current_forms.some((f1) => ObserverCommon.compareValues(f1.name, f2.name) && ObserverCommon.compareValues(f1.id, f2.id)));

    if (diffForms.length) {
      this.current_forms = this.current_forms.concat(diffForms);
    }

    // this.initShadows();
  }

  /**
   * Get the real attribute name
   */
  getBaseAttrName(el: any, attrName: string): string {
    if (attrName === 'ng-reflect-model') {
      return 'value';
    }

    if (attrName === 'value' || attrName === 'text') {
      const tag = (el.nodeName || el.tagName).toLowerCase();
      return 'type' in el && tag !== 'select'
        ? 'value'
        : 'text';
    }

    return attrName.replace('aria-', '');
  }

  /**
   * Process the specified element for validation
   */
  validateElement(el: any, attr: string, assert?: boolean, condition?: ECondition): string | undefined {
    if (!el) {
      return 'Element not provided';
    }

    if (this.logging) {
      console.group('validateElement');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'validate', EAlertType.info, this.logLevels, { role: this.getNodeRole(el), el });

    let _el = el;

    if (_el.nodeType !== Node.ELEMENT_NODE) {
      return `Element is not valid (${_el.nodeType})`;
    }

    if (this.getNodeRole(_el) !== 'page' && this.ignoreEvent(_el)) {
      return 'Cannot validate BODY Element';
    }

    let _attr = condition === ECondition.exists
      ? attr
      : this.getBaseAttrName(_el, attr);

    if (attr === 'table') {
      const tbl = _el.closest('table, [role="table"]');
      if (!tbl) {
        return 'Table element not found';
      }

      _el = tbl;
      _attr = 'text';
    }

    if (_el.nodeName === 'LABEL') {
      const _labelTarget = this.getLabelTarget(_el);
      if (_labelTarget) {
        _el = _labelTarget;
        if (attr !== 'element') {
          _attr = this.getBaseAttrName(_el, attr);
        }
      }
    }

    if (this.isExcludedElement(_el)) {
      return 'Element is excluded';
    }

    if (_el.type === 'hidden' || (!assert && !ObserverCommon.isDisplayed(_el))) {
      return 'Element is not visible';
    }

    let _value: any;
    let _condition = condition ?? ECondition.equals;

    if (condition !== ECondition.exists) {
      switch (_attr) {
        case 'required':
        case 'readonly':
        case 'pressed':
        case 'selected':
        case 'checked':
        case 'invalid':
        case 'hidden':
        case 'expanded':
          _value = _attr === 'invalid'
            ? this.getValidAttributeValue(_el, 'aria-invalid').toLowerCase()
            : this.getValidPropertyValue(_el, _attr, true).toLowerCase();
          _condition = _value === 'true'
            ? ECondition.is
            : ECondition.isNot;
          _value = _attr;
          break;
        case 'disabled':
          _value = this.getValidPropertyValue(_el, _attr, true).toLowerCase();
          _condition = _value === 'true'
            ? ECondition.is
            : ECondition.isNot;
          _value = 'disabled';
          break;
        case 'value':
          if (!this.containsAny(_el.nodeName, this.input_types)) {
            return '';
          }
          if ((_el.type === 'radio' || _el.type === 'checkbox') && !_el.checked && _el.checked !== 'true' && !assert) {
            return;
          }
          if (_el.type === 'password') {
            return 'Cannot validate password value';
          }
          _value = _el.value;
          break;
        case 'text':
          if (attr === 'table') {
            _value = _el.innerText;
          } else if (_el.nodeName === 'SELECT') {
            _value = _el.options[_el.selectedIndex].text;
          } else {
            _value = this.getElementText(_el);
          }
          break;
        default:
          _value = _el.getAttribute(_attr);
          break;
      }
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'validate', EAlertType.verbose, this.logLevels, { _value, _attr, _condition, assert, _el });

    if (this.logging) {
      console.groupEnd();
    }

    // if (condition !== ECondition.exists && (_value == null || _value === '')) {
    //   return 'No Value to Validate';
    // }

    return this.validate(_el, {
      name: _attr,
      value: _value ?? '',
      condition: _condition,
      type: assert ? 'Assert' : 'Validate'
    });
  }

  /**
   * Get the target input using the "for" attribute or decedents
   */
  getLabelTarget(el: any): any {
    const _forId = this.getValidAttributeValue(el, 'for');
    if (_forId) {
      const _forEl = document.getElementById(_forId);
      if (_forEl) {
        return _forEl;
      }
    }

    const _inputs = el.querySelectorAll('input');
    if (_inputs.length === 1 && (_inputs[0].type === 'checkbox' || _inputs[0].type === 'radio')) {
      return _inputs[0];
    }
  }

  /**
   * Get the form element closest to the specified element
   */
  getFormElement(el: any) {
    const all_form_els = document.querySelectorAll(`[${this.form_attrs.join('],[')}]`);

    // If there are any elements at all...
    if (all_form_els.length) {
      // If only 1 element then use it, else find closest one...
      const form_el = all_form_els.length === 1
        ? all_form_els[0]
        : this.getFormElementInParents(el);

      // If no element was closest then just use the first
      if (!form_el && all_form_els.length) {
        return all_form_els[0];
      }

      return form_el;
    }
  }

  /**
   * Attempts to locate the name and/or id of the current form relative to the specified element
   */
  parseForm(el?: any): IEventForm {
    const form_el = this.getFormElement(el);
    if (form_el) {
      const f = this.getEventForm(form_el);
      if (f) {
        return f;
      }
    }

    let name: string;

    const pageTitle = document.title?.trim();
    if (pageTitle) {
      return {
        id: '',
        source: 'Title',
        name: pageTitle.replace(/\s+/gm, ' ')
      };
    }

    // Try to parse name from url query text...
    const loc = this.parseLocation(window.location.href);
    if (loc.query?.form) {
      name = loc.query.form;
    } else if (loc.page) {
      name = loc.page;
    } else {
      name = '';
    }

    if (name) {
      return {
        id: '',
        source: 'URL',
        name: name.replace(/\s+/gm, ' ')
      };
    }

    return {
      id: '',
      name: 'Unknown',
      alert: 'Form Not Found'
    };
  }

  /**
   * Determines if the specified element has a valid role
   */
  hasValidRole(el: any): boolean {
    if (!el) {
      return false;
    }

    const role = this.getNodeRole(el);
    return ObserverCommon.valid_roles.includes(role);
  }

  /**
   * Determines if the specified element has a valid click role
   */
  hasValidClickRole(el: any): boolean {
    if (!el) {
      return false;
    }

    const role = this.getNodeRole(el);
    return ObserverCommon.click_roles.includes(role);
  }

  /**
   * Gets the shorthand duration format of the specified timestamp minus the start time
   */
  formatTimeSpan(time: number): string {
    let diff = time - this.start_time;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * (1000 * 60 * 60 * 24);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff -= hours * (1000 * 60 * 60);

    const mins = Math.floor(diff / (1000 * 60));
    diff -= mins * (1000 * 60);

    const seconds = Math.floor(diff / 1000);
    diff -= seconds * 1000;

    const daysText = days ? `${days}d ` : '';
    const hoursText = hours ? `${hours}h ` : '';
    const minsText = mins ? `${mins}m ` : '';
    const secondsText = seconds ? `${seconds}s ` : '';

    return daysText + hoursText + minsText + secondsText;
  }

  /**
   *
   */
  getFieldName(label: string, group: string) {
    const fullName = group && !ObserverCommon.compareValues(group, label) ? ` - ${group}` : '';
    return (label || 'Unknown') + fullName;
  }

  /**
   * Traverses up the DOM, starting at the parent of the specified element, returning the first form
   */
  getFormElementInParents(el?: any): any {
    if (this.logging) {
      console.group('getFormElementInParents');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { el });

    if (!el) {
      return null;
    }

    let p: any;
    if (el.getRootNode) {
      const r = el.getRootNode();
      p = window.ShadowRoot && r instanceof window.ShadowRoot
        ? r.host
        : el.parentNode;
    } else {
      p = el.parentNode;
    }

    let e: any = null;

    outer: while (p && p.nodeType === Node.ELEMENT_NODE && p !== document && p.nodeName !== 'HTML') {
      if (this.hasFormAttributes(p)) {
        e = p;
        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { msg: 'Ancestor has form attribute', e });
        break outer;
      }

      for (const a of this.form_attrs) {
        e = p.querySelector(`:scope > [${a}]`);
        if (e) {
          ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { msg: 'Ancestor has direct decedent form attribute', e });
          break outer;
        }
      }

      let owner: any;
      const o = this.getValidAttributeValue(p, 'aria-ownedby') || this.getValidAttributeValue(p, 'aria-flowto');
      if (o) {
        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { msg: 'Ancestor has owner', p, o });
        owner = document.querySelector(`[id="${o}"], [ng-reflect-id="${o}"]`);
      } else if (p.shadowRoot) {
        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { msg: 'Ancestor is shadow root', p });
        owner = p.shadowRoot.host.parentNode;
      }

      p = owner ?? p.parentNode;
      if (p.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        p = p.host;
      }
    }

    if (this.logging) {
      console.groupEnd();
    }

    return e;
  }

  /**
   * Performs a partial match of form attributes against the specified element
   */
  hasFormAttributes(el: any): boolean {
    const that = this;
    return [].slice.call(el.attributes).some((attr: any) => that.form_attrs.includes(attr.name) && attr.value);
  }

  /**
   * Extracts all the parts of url
   * IE Compatible alternative to new URL()
   */
  parseLocation(url: string): ILocation {
    const query: { [key: string]: string } = {};
    const parser = document.createElement('a');
    parser.href = url;

    if (parser.search) {
      const queries = parser.search.replace(/^\?/, '').split('&');

      for (let s: string[], i = 0; i < queries.length; i++) {
        s = queries[i].split('=');
        query[s[0]] = s[1];
      }
    }

    let page = '';
    if (parser.pathname) {
      const last = parser.pathname.lastIndexOf('/') + 1;
      page = parser.pathname.slice(last);

      const ext = page.lastIndexOf('.');
      if (ext > -1) {
        page = page.slice(0, ext);
      }
    }

    return {
      protocol: parser.protocol,
      host: parser.host,
      hostname: parser.hostname,
      port: parser.port,
      path: parser.pathname,
      search: parser.search,
      href: url,
      hash: parser.hash,
      query,
      page
    };
  }

  /**
   *
   */
  isExcludedElement(el: any): boolean {
    if (this.excluded_selectors.length) {
      for (const selector of this.excluded_selectors) {
        if (el.matches(selector)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Get all Forms (elements containing attributes data-form-id or data-form-name)
   */
  getAllForms(visibleOnly?: boolean): IEventForm[] {
    const all_form_els = document.querySelectorAll(`[${this.form_attrs.join('],[')}]`);
    const els: IEventForm[] = [];

    for (let i = 0, len = all_form_els.length; i < len; i++) {
      const form_el = all_form_els[i];

      if (visibleOnly && !ObserverCommon.isDisplayed(form_el)) {
        continue;
      }

      let eid = '';
      let name = '';
      let source: string | undefined;

      if (form_el[this.id_attr]) {
        eid = form_el[this.id_attr];
      } else if (form_el.hasAttribute(this.id_attr)) {
        eid = this.getValidAttributeValue(form_el, this.id_attr);
      }

      if (form_el[this.name_attr]) {
        name = form_el[this.name_attr];
      } else if (form_el.hasAttribute(this.name_attr)) {
        name = this.getValidAttributeValue(form_el, this.name_attr) || form_el.textContent || '';
      }

      if (name) {
        name = name.replace(/\s+/gm, ' ');
        source = 'Attribute';
      }

      name = name || 'Unknown';
      eid = eid || '';

      const existingElem = els.find((elem) => name.toLowerCase() === elem.name.toLowerCase() || (eid && eid === elem.id));
      if (!existingElem) {
        els.push({
          id: eid,
          alert: name ? '' : 'Form Not Found',
          rec: form_el.getBoundingClientRect(),
          name,
          source
        });
      }
    }

    const pageTitle = document.title?.trim();
    if (pageTitle) {
      els.push({
        id: '',
        source: 'Title',
        name: pageTitle.replace(/\s+/gm, ' '),
        rec: {
          x: 0,
          y: Number.MAX_SAFE_INTEGER,
          width: 0,
          height: 0,
          bottom: 0,
          left: 0,
          right: 0,
          top: Number.MAX_SAFE_INTEGER,
          toJSON: () => { }
        }
      });
    }

    return els;
  }

  /**
   * Get the top Form element based on the lowest top value of getBoundingClientRect
   */
  getTopForm(forms: IEventForm[], ignore?: string[]): IEventForm | null {
    const _ignored = ignore?.length ?
      ignore.map((value) => value.toLocaleLowerCase().replace(/\s+/gm, ' ')) :
      [];
    const _forms = _ignored.length
      ? forms.filter((frm) => !_ignored.includes(frm.name.toLocaleLowerCase().replace(/\s+/gm, ' '))
        && (!frm.id || !_ignored.includes(frm.id?.toLocaleLowerCase().replace(/\s+/gm, ' '))))
      : forms;

    const len = _forms.length;
    if (!len) {
      if (_ignored.length) {
        return this.getTopForm(forms);
      }

      return null;
    }

    let top = _forms[0];

    for (let i = 1; i < len; i++) {
      const f = _forms[i];
      if ((f.rec?.top || 0) < (top.rec?.top || 0)) {
        top = f;
      }
    }

    return top;
  }

  /**
   * Get the field definition
   */
  getFieldDefinition(name?: string, role?: string): IFieldDefinition | undefined {
    return this.field_definitions
      .find((d) => (name && name.toLowerCase() === d.name.toLowerCase() && d.type === 'tag') ||
        (role && role.toLowerCase() === d.name.toLowerCase() && d.type === 'role'));
  }

  /**
   * Get the role of the specified element
   */
  getNodeRole(el: any): string {
    if (!el || !el.getAttribute) {
      return '';
    }

    const tag = (el.nodeName || el.tagName).toLowerCase();
    let role = this.getValidAttributeValue(el, 'role') || this.getValidAttributeValue(el, 'data-field-type') || '';

    if (role) {
      role = role.replace(/[-_ ]+/g, '');
    }

    if (!role) {
      const def = this.getFieldDefinition(el.nodeName);
      if (def) {
        role = def.role ?? '';
      }
    }

    if (!role) {
      role = ObserverCommon.tag_roles[tag];
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'getNodeRole', EAlertType.info, this.logLevels, { class: typeof el.type, type: el.type, el });

    if (!role && tag === 'input') {
      switch (el.type) {
        case 'checkbox':
          role = 'checkbox';
          break;
        case 'radio':
          role = 'radiobutton';
          break;
        case 'button':
        case 'reset':
        case 'submit':
          role = 'button';
          break;
        default:
          role = 'inputbox';
          break;
      }
    }

    if (role === 'dropdown') {
      role = 'dropdownlist';
    }

    if (role) {
      return role;
    }

    role = el.type ? el.type.toString().toLowerCase() : '';
    role = this.containsAny(role, ObserverCommon.valid_roles) ?? '';

    if (role === 'dropdown') {
      role = 'dropdownlist';
    }

    if (!role && el.isContentEditable) {
      role = 'textarea';
    }

    return role;
  }

  /**
   *
   */
  containsAny(value: string, arr: any[]): string | null {
    if (!arr.length || value === null || value === undefined) {
      return null;
    }

    const v = (value || '').toLowerCase();

    for (const item of arr) {
      const elv = (item ?? '').toLowerCase();

      if (v === elv || v.includes(elv)) {
        return elv;
      }
    }

    return null;
  }

  /**
   * Get all parent elements of the specified element
   */
  getParents(el: any, matchType?: EMatch, props?: INodeProps[]) {
    const parents: any[] = [];
    let p = el.parentNode;
    let matched = false;

    while (p && p.nodeName !== 'BODY' && p.nodeName !== 'HTML' && (p.nodeType === Node.ELEMENT_NODE || p.nodeType === Node.DOCUMENT_FRAGMENT_NODE)) {
      if (p.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        p = p.host; // .parentNode;
      }

      parents.push(p);

      if (props?.length) {
        let matches = 0;
        const req = props.filter((p) => p.required).length;
        let required_matches = 0;

        for (const o of props) {
          const pn = o.name;
          const pv = o.value.toLowerCase();
          const op = o.operator || EOperator.Equals;
          const el_prop = ((pn === 'role' ? this.getNodeRole(p) : p[pn]) ?? '').toString().toLowerCase();
          let m = false;

          switch (op) {
            case EOperator.Equals:
              if (el_prop == pv) {
                m = true;
              }
              break;
            case EOperator.EqualsStrict:
              if (el_prop === pv) {
                m = true;
              }
              break;
            case EOperator.NotEquals:
              if (el_prop != pv) {
                m = true;
              }
              break;
            case EOperator.NotEqualsStrict:
              if (el_prop !== pv) {
                m = true;
              }
              break;
            case EOperator.GreaterThan:
              if (el_prop > pv) {
                m = true;
              }
              break;
            case EOperator.GreaterOrEqual:
              if (el_prop >= pv) {
                m = true;
              }
              break;
            case EOperator.LessThan:
              if (el_prop < pv) {
                m = true;
              }
              break;
            case EOperator.LessOrEqual:
              if (el_prop <= pv) {
                m = true;
              }
              break;
            case EOperator.Contains:
              if (el_prop.includes(pv)) {
                m = true;
              }
              break;
            case EOperator.NotContains:
              if (!el_prop.includes(pv)) {
                m = true;
              }
              break;
            default:
              break;
          }

          if (m) {
            matches++;
            if (o.required) {
              required_matches++;
            }
          }
        }

        if (required_matches === req && ((matchType === EMatch.Any && matches > 0) || (matchType === EMatch.All && matches === props.length))) {
          matched = true;
          break;
        }
      }

      p = p.parentNode;
    }

    return { parents, matched };
  }

  /**
   * Get the x and y position relative to the viewport
   */
  getElementCoordinates(el: any): string {
    const elementInfo = ObserverCommon.getElementInfo(el, true);
    if (!elementInfo.width || !elementInfo.height) {
      return '';
    }

    const contentLevel = {
      width: elementInfo.width,
      height: elementInfo.height
    };
    const paddingLevel = {
      width: elementInfo['padding-left'] + contentLevel.width + elementInfo['padding-right'],
      height: elementInfo['padding-top'] + contentLevel.height + elementInfo['padding-bottom']
    };
    const borderLevel = {
      width: elementInfo['border-left-width'] + paddingLevel.width + elementInfo['border-right-width'],
      height: elementInfo['border-top-width'] + paddingLevel.height + elementInfo['border-bottom-width']
    };
    const marginLevel = {
      width: elementInfo['margin-left'] + borderLevel.width + elementInfo['margin-right'],
      height: elementInfo['margin-top'] + borderLevel.height + elementInfo['margin-bottom']
    };

    return `left:${elementInfo.left}px;top:${elementInfo.top}px;width:${marginLevel.width}px;height:${marginLevel.height}px;`;
  }

  /**
   * Get the numeric position of the specified element
   */
  getPositionByXpath(el: any, labelSource: string, parentField?: IField, xpath?: string): IPosition {
    ObserverCommon.consoleData(this.logging, BuildVersion, 'getPositionByXpath', EAlertType.info, this.logLevels, { el, labelSource, parentField, xpath });

    const position: IPosition = {
      nodes: 0,
      type: 'xpath'
    };

    if (!xpath) {
      position.error = 'No XPath';
      return position;
    }

    let _el = el;
    const _parentRole = parentField?.type ?? this.getNodeRole(_el.parentNode);
    const _rowChild = _parentRole === 'cell' || _parentRole === 'row';
    if (labelSource === 'Table Header') {
      if (_rowChild) {
        _el = _el.closest('tr,[role="row"]');
      }
      this.getPositionByRow(position, _el);
    }

    try {
      const els: any[] = [];
      const nodes = document.evaluate(xpath, document.body, null, XPathResult.ANY_TYPE, null);
      let node = nodes.iterateNext();
      while (node) {
        if (ObserverCommon.isDisplayed(node)) {
          els.push(node);
        }
        node = nodes.iterateNext();
      }
      position.nodes = els.length;

      if (position.total === undefined || position.ordinal === undefined) {
        position.total = els.length;
        const index = els.indexOf(_el);
        if (index >= 0) {
          position.ordinal = index + 1;
        }
      }
    } catch (error: unknown) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'getPositionByXpath', EAlertType.error, this.logLevels, { error });
      position.error = (error as any)?.toString();
    }

    return position;
  }

  /**
   * Get the numeric position of the specified element
   */
  getPositionByCss(el: any, root: ShadowRoot, css?: string): IPosition {
    let ordinal: number | undefined;
    let nodes = 0;
    let total = 0;
    let error: string | undefined;

    if (!css) {
      return {
        type: 'css',
        error: 'No CSS',
        nodes,
        total,
        ordinal
      };
    }

    try {
      const els = root.querySelectorAll(css);
      total = els.length;
      nodes = els.length;
      const index = Array.from(els).indexOf(el);
      if (index >= 0) {
        ordinal = index + 1;
      }
    } catch (error: unknown) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'getPositionByCss', EAlertType.info, this.logLevels, { error });
      error = (error as any)?.toString();
    }

    return {
      type: 'css',
      nodes,
      total,
      ordinal,
      error
    };
  }

  /**
   *
   */
  getPositionByRow(position: IPosition, el: any): IPosition {
    ObserverCommon.consoleData(this.logging, BuildVersion, 'getPositionByRow', EAlertType.info, this.logLevels, { position, el });

    const table = el?.closest('table,[role="table"]');
    if (!table) {
      return position;
    }

    const rows = table.querySelectorAll('tr,[role="row"]');
    let total = 0;
    let ordinal = 0;

    for (const row of rows) {
      if (row.parentNode.tagName === 'THEAD') {
        continue;
      }

      const cells = row.querySelectorAll('td:not([role="columnheader"]),[role="cell"],[role="gridcell"],[role="rowheader"]');
      if (cells.length) {
        total++;
        ordinal++;
        if (row.isSameNode(el)) {
          position.ordinal = ordinal;
        }
      }
    }

    if (total > 0) {
      position.total = total;
    }

    return position;
  }

  /**
   *
   */
  getPositionByCell(position: IPosition, el: any): IPosition {
    const table = el?.closest('table,[role="table"]');
    if (!table) {
      return position;
    }

    const rows = table.querySelectorAll('tr,[role="row"]');
    let total = 0;

    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const cells = row.querySelectorAll('td,[role="cell"]');
      if (cells.length) {
        total++;
        for (const cell of cells) {
          if (cell.isSameNode(el)) {
            position.ordinal = r + 1;
          }
        }
      }
    }

    if (total > 0) {
      position.total = total;
    }

    return position;
  }

  /**
   * Get the unique identifier of an element based on specific attributes in a preferred order
   */
  getElementID(el: any): IElementID {
    let attr = 'data-form_line_id';
    let val = this.getValidAttributeValue(el, attr);

    if (!val) {
      attr = 'evolv-id';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'data-automation';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'testid';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'id';
      val = el.id;
    }

    if (!val) {
      attr = 'ng-reflect-id';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'ng-reflect-name';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'aria-owns';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'aria-controls';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'aria-flowto';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'aria-ownedby';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'for';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = 'ng-reflect-value';
      val = this.getValidAttributeValue(el, attr);
    }

    if (!val) {
      attr = '';
    }

    return {
      attribute: attr,
      value: val
    };
  }

  /**
   *
   */
  getXpathID(id: IElementID): string {
    const regex = /([^~,]+)(~([a-z0-9\.]+))*(,([a-z0-9\.]+)(,([a-z0-9\.]+))*)*/i;
    let m: RegExpExecArray | null = null;

    if ((m = regex.exec(id.value)) === null) {
      return `//*[@${id.attribute}="${id.value}"]`;
    }

    const radId: IIdentifier = {
      Field: m[1],
      Unknown: m[3],
      Table: m[5],
      Indirect: m[7]
    };

    if (radId.Field === id.value) {
      return `//*[@${id.attribute}="${radId.Field}"]`;
    }

    let ind = '';
    if (radId.Indirect) {
      ind = ` and substring(@${id.attribute}, string-length(@${id.attribute}) - ${radId.Indirect.length} + 1) = "${radId.Indirect}"`;
    }

    return `//*[starts-with(@${id.attribute}, "${radId.Field}~")${ind}]`;
  }

  /**
   *
   */
  getCssID(id: IElementID): string {
    const regex = /([^~,]+)(~([a-z0-9\.]+))*(,([a-z0-9\.]+)(,([a-z0-9\.]+))*)*/i;
    let m: RegExpExecArray | null = null;

    if ((m = regex.exec(id.value)) === null) {
      if (id.attribute === 'id') {
        return `#${id.value}`;
      }
      return `[${id.attribute}="${id.value}" i]`;
    }

    const radId: IIdentifier = {
      Field: m[1],
      Unknown: m[3],
      Table: m[5],
      Indirect: m[7]
    };

    if (radId.Field === id.value) {
      return `[${id.attribute}="${radId.Field}" i]`;
    }

    let ind = '';
    if (radId.Indirect) {
      ind = `[${id.attribute}$="${radId.Indirect}" i]`;
    }

    return `[${id.attribute}^="${radId.Field}~" i]${ind}`;
  }

  /**
   * Gets the XPath
   */
  getXpath(el: any, parents: any[], isParent?: boolean): IXPath {
    let _id = this.getElementID(el);
    if (_id.value) {
      return {
        path: this.getXpathID(_id),
        source: 'ID',
      };
    }

    const idx = (sib: any, name?: string): any => sib
      ? idx(sib.previousElementSibling, name || sib.nodeName) + (sib.nodeName == name)
      : 1;

    const segments: string[] = [];
    let xid = '';

    for (const pp of parents) {
      if (pp.nodeName === 'BODY') {
        break;
      }

      _id = this.getElementID(pp);

      if (_id.value) {
        xid = this.getXpathID(_id);
        break;
      }

      const ppIdx = idx(pp);
      const idxText = ppIdx > 0 ? `[${ppIdx}]` : '';
      segments.push(`${pp.nodeName.toLowerCase()}${idxText}`);
    }

    let last_prefix = '';
    let last_node = '';
    let _text = el.innerText?.replace(/"/gm, '\\"') ?? '';

    const first_node = xid || `//${segments.join('/')}`;
    const node_name = el.nodeName.toLowerCase();
    const el_role = this.getValidAttributeValue(el, 'role');
    const el_ph = this.getValidAttributeValue(el, 'placeholder');
    const el_ce = el.isContentEditable;

    if (this.click_inputs.includes(el.type) && el.value) {
      last_node = `${node_name}[@value="${el.value}"]`;
      last_prefix = first_node === '//'
        ? ''
        : '//';

      return {
        path: first_node + last_prefix + last_node,
        source: 'Value'
      };
    }

    if (this.table_tags.includes(node_name) || ObserverCommon.table_roles.includes(el_role)) {
      const elIdx = idx(el);
      const idxText = elIdx > 0 ? `[${elIdx}]` : '';
      last_node = `${node_name}${idxText}`;

      if (first_node === '//') {
        last_prefix = '';
      } else if (first_node === xid) {
        last_prefix = `/${segments.join('/')}/`;
      } else {
        last_prefix = '/';
      }

      return {
        path: first_node + last_prefix + last_node,
        source: 'Path'
      };
    }

    if (!isParent && !el_ce && (_text || el.textContent)) {
      const role_text = el_role ? ` and @role="${el_role}"` : '';
      const _html = el.innerHTML;
      const _xFunc = _html.includes('&nbsp;') // Contains Alt+0160 character
        ? 'translate(normalize-space(), " ", "")'
        : 'normalize-space()';

      if (_text) {
        if (_text.includes('\n')) {
          _text = _text.replace(/\s+/gm, ' ').trim();
          last_node = `${node_name}[contains(${_xFunc}, "${_text}")${role_text}]`;
        } else {
          _text = _text.trim();
          last_node = `${node_name}[${_xFunc}="${_text}"${role_text}]`;
        }

        last_prefix = first_node === '//'
          ? ''
          : '//';
      } else if (el.textContent) {
        _text = el.textContent.replace(/"/gm, '\\"').replace(/\s+/gm, ' ');

        if (_text.length > 50) {
          _text = _text.slice(0, 50).trim();
          last_node = `${node_name}[starts-with(${_xFunc},"${_text}")${role_text}]`;
        } else {
          _text = _text.trim();
          last_node = `${node_name}[${_xFunc}="${_text}"${role_text}]`;
        }

        last_prefix = first_node === '//'
          ? ''
          : '//';
      }

      return {
        path: first_node + last_prefix + last_node,
        source: 'Text'
      };
    }

    if (el_role) {
      last_node = `${node_name}[@role='${el_role}']`;
      last_prefix = first_node === '//'
        ? ''
        : '//';

      return {
        path: first_node + last_prefix + last_node,
        source: 'Role'
      };
    }

    if (el_ph) {
      last_node = `${node_name}[@placeholder='${el_ph}']`;
      last_prefix = first_node === '//'
        ? ''
        : '//';

      return {
        path: first_node + last_prefix + last_node,
        source: 'Placeholder'
      };
    }

    const elIdx = idx(el);
    const idxText = elIdx > 0 ? `[${elIdx}]` : '';
    last_node = `${node_name}${idxText}`;
    last_prefix = first_node === '//'
      ? ''
      : (first_node === xid ? '//' : '/');

    return {
      path: first_node + last_prefix + last_node,
      source: 'Path'
    };
  }

  /**
   * Gets the CSS
   */
  getCss(el: any): string {
    const _id = this.getElementID(el);
    if (_id.value) {
      return this.getCssID(_id);
    }

    return ObserverCommon.getCssPath(el, true);
  }

  /**
   * Gets the XPath and CSS selectors
   */
  getSelectors(el: any, parents: any[], isParent?: boolean): ISelectors {
    const _selectors: ISelectors = {};

    const roots = {};
    const _parents: any[] = [];
    let rootname = '';
    let rootcount = 0;

    for (let s = 0, p = parents.length; s < p; s++) {
      const parent = parents[s];
      if (parent.host && parent.nodeName !== 'A') {
        continue;
      }

      if (parent.shadowRoot) {
        rootname = parent.nodeName;
        roots[rootname] = {
          node: parent,
          parents: []
        };
        rootcount++;
      } else if (rootname) {
        roots[rootname].parents.push(parent);
      } else {
        _parents.push(parent);
      }
    }

    try {
      _selectors.css = this.getCss(el);

      const _xpath = this.getXpath(el, _parents, isParent);
      _selectors.xpath = _xpath.path;
      _selectors.xpath_source = _xpath.source;
    } catch (error: unknown) {
      ObserverCommon.consoleData(this.logging, BuildVersion, 'getSelectors', EAlertType.error, this.logLevels, { error });
    }

    if (rootcount > 0) {
      const xpaths: IXPath[] = [];
      const csspaths: string[] = [];

      for (const key in roots) {
        const root = roots[key];

        xpaths.unshift(this.getXpath(root.node, root.parents));

        try {
          csspaths.unshift(this.getCss(root.node));
        } catch (error: unknown) {
          ObserverCommon.consoleData(this.logging, BuildVersion, 'getSelectors', EAlertType.error, this.logLevels, { error });
        }
      }

      _selectors.shadowXpath = xpaths.map((p) => p.path).join(';;');
      _selectors.shadowXpath_source = xpaths.map((p) => p.source).join(', ');

      try {
        _selectors.shadowCss = csspaths.join(';;');
      } catch (error: unknown) {
        ObserverCommon.consoleData(this.logging, BuildVersion, 'getSelectors', EAlertType.error, this.logLevels, { error });
      }
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'getSelectors', EAlertType.verbose, this.logLevels, { _selectors });

    return _selectors;
  }

  /**
   * Get alert for invalid XPath and CSS selector
   */
  getFieldAlerts(position: IPosition, selectors: ISelectors, label: ILabel, role?: string): IFieldAlert | undefined {
    const posType = ObserverCommon.toProperCase(position.type);

    if (position.type === 'xpath' && !selectors.xpath) {
      return {
        message: 'XPath could not be calculated',
        type: EAlertType.error
      };
    }

    if (position.type === 'css' && !selectors.css) {
      return {
        message: 'CSS could not be calculated',
        type: EAlertType.error
      };
    }

    if (position.error) {
      return {
        message: `Error evaluating ${posType}: ${position.error}`,
        type: EAlertType.error
      };
    }

    if (position.nodes === 0) {
      return {
        message: `No elements found with calculated ${posType}`,
        type: EAlertType.error
      };
    }

    if (position.nodes > 1) {
      return {
        message: `Found ${position.nodes} elements with calculated ${posType}`,
        type: EAlertType.warn
      };
    }

    if (label.source === 'Inner Text' && !this.text_label_roles.includes(role ?? '')) {
      return {
        message: 'Unreliable Label Source of innerText.',
        type: EAlertType.warn
      };
    }

    if (label.source === 'Location') {
      return {
        message: 'Unreliable Label Source of DOM location.',
        type: EAlertType.warn
      };
    }

    if (label.source === 'Name') {
      return {
        message: 'Unreliable Label Source of name attribute.',
        type: EAlertType.warn
      };
    }

    if (label.source === 'ID') {
      return {
        message: 'Unreliable Label Source of id attribute.',
        type: EAlertType.warn
      };
    }
  }

  /**
   * Ignore events
   */
  ignoreEvent(el: any): boolean {
    if (this.ignore_event) {
      this.ignore_event = false;
      return true;
    }

    const nodename = (el.nodeName || '').toUpperCase();
    if (!nodename || nodename.includes('document') || nodename === 'BODY' || nodename === 'HTML') {
      return true;
    }

    return false;
  }

  /**
   *
   */
  getValidAttributeValue(el: any, attr: string): string {
    let val = '';
    if (!el || !el.getAttribute) {
      return val;
    }

    const attrVal = el.getAttribute(attr);

    if (attrVal != null && attrVal !== '[object Object]') {
      val = attrVal.toString().trim();
    }

    return val;
  }

  /**
   *
   */
  getValidPropertyValue(el: any, prop: string, state?: boolean): string {
    let val = '';
    if (!el) {
      return val;
    }

    let propVal = el ? el[prop] : null;

    if (!state) {
      if (propVal != null && propVal !== '[object Object]') {
        val = propVal.toString().trim();
      }

      return val;
    }

    propVal = propVal ?? 'false';

    if (propVal === true || propVal === 'true' || ObserverCommon.compareValues(propVal, prop)) {
      propVal = 'true';
    }

    let ariaVal = el[`aria${ObserverCommon.toProperCase(prop)}`] ?? 'false';

    if (ariaVal === true || ariaVal === 'true' || ObserverCommon.compareValues(ariaVal, prop)) {
      ariaVal = 'true';
    }

    if (ariaVal === 'true' || propVal === 'true') {
      val = 'true';
    } else if (prop === 'disabled') {
      const matched = this.getParents(el, EMatch.Any, [
        { name: 'disabled', value: 'true' },
        { name: 'ariaDisabled', value: 'true' }
      ]).matched;
      if (matched) {
        val = 'true';
      }
    }

    return val;
  }

  /**
   *
   */
  hasElements(el: any): boolean {
    let child = el.firstChild;
    let has_element = false;

    while (child) {
      if (child.nodeType === Node.ELEMENT_NODE && !this.markup_tags.includes(child.nodeName)) {
        has_element = true;
        break;
      }

      child = child.nextSibling;
    }

    return has_element;
  }

  /**
   * Checks if the node is a document node or not.
   */
  isDocumentNode(node: any): boolean {
    return node && (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE || node.nodeType === Node.DOCUMENT_NODE);
  }

  /**
   *
   */
  getParentOrHost(element: any, root?: any) {
    const parentNode = element.parentNode;

    if (parentNode?.host && parentNode?.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      return parentNode.host;
    }

    if (parentNode === root) {
      return null;
    }

    return parentNode;
  }

  /**
   *
   */
  getSelectedOptionText(el: any): string {
    if (!el.options || !el.options.length || el.selectedIndex === undefined || el.selectedIndex < 0) {
      return '';
    }

    return el.options[el.selectedIndex] ?
      el.options[el.selectedIndex].text || '' :
      '';
  }

  /**
   *
   */
  getElementText(el: any, role?: string): string {
    let txt = this.getValidAttributeValue(el, 'text');
    if (txt) {
      return txt;
    }

    if (!el.childNodes.length) {
      txt = '';
    } else if (el.nodeName === 'LABEL' || el.nodeName === 'LEGEND' || !this.hasElements(el)) {
      txt = el.innerText.trim();
    } else {
      let child = el.firstChild;
      const texts: string[] = [];

      if (role === undefined) {
        role = this.getNodeRole(el);
      }

      while (child) {
        if (child.nodeType === Node.TEXT_NODE) {
          texts.push(child.data);
        } else if (child.nodeType === Node.ELEMENT_NODE && !this.list_containers.includes(role) && !this.list_tags.includes(child.tagName)) {
          texts.push(child.textContent);
        }

        child = child.nextSibling;
      }

      txt = texts.join('').trim();
    }

    return txt;
  }

  /**
   *
   */
  getDeepText(el: any): string {
    let finalText = '';

    if (this.hasElements(el)) {
      let children = el.children;

      while (children && children.length) {
        if (!el) {
          break;
        }

        let txt = '';
        const texts: string[] = [];

        if (this.hasElements(el)) {
          if (this.markup_tags.includes(el.nodeName)) {
            texts.push(el.textContent);
          } else {
            let child = el.firstChild;

            while (child) {
              if (child.nodeType === Node.TEXT_NODE) {
                texts.push(child.data);
              } else if (child.nodeType === Node.ELEMENT_NODE && this.markup_tags.includes(child.nodeName)) {
                texts.push(child.textContent);
              }

              child = child.nextSibling;
            }
          }
        } else {
          txt = el.innerText.trim();
        }

        if (!txt) {
          txt = texts.join('').trim();
        }

        if (txt) {
          finalText = txt;
          break;
        }

        children = el.children;
        el = null;
        for (const child of children) {
          if (child.innerText?.trim()) {
            el = child;
            break;
          }
        }
      }
    } else {
      return el.innerText.trim();
    }

    return finalText;
  }

  /**
   * Get the aria-label attribute value or the value of the aria-labelledby element
   */
  getAriaLabel(node: any): string {
    const text = this.getValidAttributeValue(node, 'aria-label').trim();

    if (!text) {
      return this.getAriaLabelledBy(node);
    }

    return text;
  }

  /**
   *
   */
  getAriaLabelledBy(node: any): string {
    let text = '';
    const identifier = this.getValidAttributeValue(node, 'aria-labelledby');
    const noGroup = node.nodeName === 'TABLE' || node.role === 'table';

    if (identifier) {
      let labelEl = document.getElementById(identifier);
      if (labelEl && (!noGroup || !this.isTableGroup(labelEl))) {
        text = (labelEl.innerText || labelEl.textContent || '').replace(/\s+/gm, ' ').trim();
      }

      if (!text) {
        const identifierLowered = identifier.toLowerCase();
        labelEl = document.getElementById(identifierLowered);
        if (labelEl && (!noGroup || !this.isTableGroup(labelEl))) {
          return (labelEl.innerText || labelEl.textContent || '').replace(/\s+/gm, ' ').trim();
        }
      }
    }

    return text;
  }

  /**
   *
   */
  isTableGroup(el: any): boolean {
    if (!el) {
      return false;
    }

    const node = el.nodeName;
    const role = this.getValidAttributeValue(el, 'role');

    return node === 'TABLE' ||
      node === 'THEAD' ||
      node === 'TBODY' ||
      node === 'TR' ||
      role === 'table' ||
      role === 'rowgroup' ||
      role === 'row';
  }

  /**
   *
   */
  getLabelFor(node: any, attribute: string) {
    const identifier = node.getAttribute(attribute) || node[attribute] || '';
    let text = '';
    let source = '';

    if (identifier) {
      let labelEl: HTMLElement | null = document.querySelector(`label[for="${identifier}"]`);
      if (labelEl) {
        text = (labelEl.innerText || labelEl.textContent || '').replace(/\s+/gm, ' ').trim();
      }

      if (text) {
        source = 'Label For';
      } else {
        const identifierLowered = identifier.toLowerCase();
        const filter = Array.prototype.filter;
        const result = document.querySelectorAll('label[for]');
        const labels = filter.call(result, (node: HTMLElement) => node.getAttribute('for')?.toLowerCase() === identifierLowered);

        labelEl = this.getClosestSibling(node, labels);
        if (labelEl) {
          text = (labelEl.innerText || labelEl.textContent || '').replace(/\s+/gm, ' ').trim();
          source = 'Closest Label For';
        }
      }
    }

    return { text, source };
  }

  /**
   *
   */
  getClosestSibling(node: any, siblings: any): HTMLElement | null {
    const copies: any[] = [].slice.call(siblings);
    copies.unshift(node);

    const copiesLength = copies.length;
    let closest = 1;

    if (closest > copiesLength) {
      closest = 0;
    }

    const nodeIndex = 0;
    let siblingIndex = closest;

    for (let i = 0; i < copiesLength; i++) {
      const delta = Math.abs(nodeIndex - i);

      if (delta > closest) {
        closest = delta;
        siblingIndex = i;
      }
    }

    return siblingIndex === 0 ? null : copies[siblingIndex];
  }

  /**
   *
   */
  getFriendlyID(id: string): string {
    if (!id) {
      return '';
    }

    let str = id.replace(/[^a-zA-Z\d]/g, ' ');
    str = str.replace(/([a-z\xE0-\xFF])([A-Z\xC0\xDF])/g, '$1 $2');
    str = str.toLowerCase().replace(/^\w|\s\w/g, (c: string) => c.toUpperCase());

    return str.trim();
  }

  /**
   *
   */
  getGroupLabel(el: any, label: string, role: string, attrs: IAttributes) {
    let groupLabel = '';

    if (role === 'table' || el.nodeName === 'TABLE') {
      return groupLabel;
    }

    const fieldset = el.closest('fieldset, [role="fieldset"]');
    if (fieldset) {
      const legend = fieldset.querySelector('legend, [role="legend"]');
      if (legend) {
        groupLabel = (legend.innerText || legend.textContent || '').replace(/\s+/gm, ' ').trim();
      }
    }

    if (
      !groupLabel &&
      label &&
      role?.includes('radio') &&
      attrs.value !== 'on'
    ) {
      if (!ObserverCommon.compareValues(label, attrs.value)) {
        groupLabel = label;
      } else if (attrs.name) {
        groupLabel = this.getFriendlyID(attrs.name);
      }
    }

    if (groupLabel?.endsWith('*')) {
      groupLabel = groupLabel.slice(0, -1).trim();
    }

    return groupLabel;
  }

  /**
   *
   */
  childrenMatches(el: any, selector: string) {
    return Array.prototype.filter.call(el.children, (child: any) => child.matches(selector));
  }

  /**
   *
   */
  getTableLabel(node: any) {
    let source = '';
    let text = node.querySelector('caption, [role="caption"]')?.textContent?.trim();
    if (text) {
      source = 'Caption';
    } else {
      text = this.getValidPropertyValue(node, 'summary');
      if (text) {
        source = 'Summary';
      }
    }

    if (!text) {
      const fieldset = node.closest('fieldset, [role="fieldset"]');
      if (fieldset) {
        const legend = fieldset.querySelector('legend, [role="legend"]');
        if (legend) {
          text = (legend.innerText || legend.textContent || '').replace(/\s+/gm, ' ').trim();
        }
      }
    }

    if (!text && node.name) {
      text = this.getFriendlyID(node.name);
      if (text) {
        source = 'Name';
      }
    }

    if (!text) {
      const _tables = this.getAllTableElements(node);
      if (_tables.length) {
        const index = _tables.indexOf(node);
        if (index >= 0) {
          text = `Table ${index + 1}`;
          source = 'Location';
        }
      }
    }

    if (!text) {
      text = 'Unknown';
      source = 'none';
    }

    return { text, source };
  }

  /**
   * Get the label for the specified element
   */
  getLabelText(node: any, role: string, nodeText: string): ILabel {
    let source = '';
    let text = this.getAriaLabel(node);
    const def = this.getFieldDefinition(node.nodeName, role);

    if (text) {
      source = 'Aria Label';
    }

    if (!text && node.nodeName === 'LABEL') {
      text = (this.getElementText(node, role) || '').replace(/\s+/gm, ' ').trim();
      if (text) {
        source = 'Inner Text';
      }
    }

    const _id = this.getElementID(node);
    if (!text && _id) {
      const obj = this.getLabelFor(node, _id.attribute);
      if (obj.text) {
        text = obj.text;
        source = obj.source;
      }
    }

    if (!text && node.name) {
      const obj = this.getLabelFor(node, 'name');
      if (obj.text) {
        text = obj.text;
        source = obj.source;
      }
    }

    if (!text && (role === 'table' || role === 'grid' || node.nodeName === 'TABLE')) {
      const tLabel = this.getTableLabel(node);
      source = tLabel.source;
      text = tLabel.text;
    }

    if (!text && role && (role.includes('link') || role.includes('button') || role.includes('item') || (role.includes('tab')))) {
      text = nodeText?.replace(/\s+/gm, ' ').trim();
      if (text) {
        source = 'Inner Text';
      }
    }

    if (!text && node.nodeName === 'INPUT' && (node.type === 'reset' || node.type === 'submit' || node.type === 'button')) {
      text = node.value?.trim();
      if (text) {
        source = 'Value';
      }
    }

    if (!text && role === 'row') {
      const rows = node.parentNode.querySelectorAll('tr');
      const rowIndex = Array.from(rows).indexOf(node);
      if (rowIndex >= 0) {
        const table = node.closest('table,[role="table"]');
        if (table) {
          const tLabel = this.getTableLabel(table);
          source = tLabel.source;
          text = tLabel.text
            ? `${tLabel.text} - Row ${rowIndex + 1}`
            : `Row ${rowIndex + 1}`;
        }
      }
    }

    if (!text && this.cell_roles.includes(role)) {
      const cells = node.parentNode.querySelectorAll('td:not([role="columnheader"]),[role="cell"],[role="gridcell"],[role="rowheader"]');
      const cellIndex = Array.from(cells).indexOf(node);
      if (cellIndex >= 0) {
        const table = node.closest('table,[role="table"]');
        if (table) {
          const headers = this.getTableHeaders(table);
          if (headers.length >= cellIndex) {
            text = headers[cellIndex]?.textContent?.trim();
            if (text) {
              source = 'Table Header';
            }
          }
        }
      }
    }

    if (!text && def && def.label) {
      let n = node;
      let p = node.parentNode;

      while (p && p !== document && p.nodeName !== 'BODY' && p.nodeName !== 'HTML') {
        if (p.nodeName === def.label) {
          if (p.nodeName === 'LABEL') {
            text = (this.getElementText(p, role) || '').replace(/\s+/gm, ' ').trim();
            if (text) {
              source = 'Closest Label (def)';
              break;
            }
          }

          const labels = this.childrenMatches(p, 'LABEL');
          if (labels.length) {
            const label = labels.length === 1 ?
              labels[0] :
              this.getClosestSibling(n, labels);

            if (label) {
              text = (this.getElementText(label, role) || '').replace(/\s+/gm, ' ').trim();
              if (text && text !== n.innerText) {
                source = 'Closest Label (def)';
                break;
              }
            }
          }
        }

        n = p;
        p = p.parentNode;
      }
    }

    if (!text) {
      let n = node;
      let p = node.parentNode;

      while (p && p !== document && p.nodeName !== 'BODY' && p.nodeName !== 'HTML') {
        if (p.nodeType === Node.ELEMENT_NODE) {
          if (p.nodeName === 'LABEL') {
            text = (this.getElementText(p, role) || '').replace(/\s+/gm, ' ').trim();

            if (text) {
              source = 'Closest Label';
              break;
            }
          }

          const labels = this.childrenMatches(p, 'LABEL');
          if (labels.length) {
            const label = this.getClosestSibling(n, labels);

            if (label) {
              text = (this.getElementText(label, role) || '').replace(/\s+/gm, ' ').trim();

              if (text && text !== n.innerText) {
                source = 'Closest Label';
                break;
              }
            }
          }
        }

        n = p;
        p = p.parentNode;
      }
    }

    if (!text) {
      const nodeParent = node.parentNode;
      const parentRole = this.getNodeRole(nodeParent);

      if (this.cell_roles.includes(parentRole)) {
        const cells = nodeParent.parentNode.querySelectorAll('td:not([role="columnheader"]),[role="cell"],[role="gridcell"],[role="rowheader"]');
        const cellIndex = Array.from(cells).indexOf(nodeParent);
        if (cellIndex >= 0) {
          const table = nodeParent.closest('table,[role="table"]');
          if (table) {
            const headers = this.getTableHeaders(table);
            if (headers.length >= cellIndex) {
              text = headers[cellIndex]?.textContent?.trim();
              if (text) {
                source = 'Table Header';
              }
            }
          }
        }
      } else if (nodeParent.tagName === 'FIELDSET') {
        const legend = nodeParent.querySelector('legend');
        if (legend) {
          text = (this.getElementText(legend) || '').replace(/\s+/gm, ' ').trim();
          if (text) {
            source = 'Closest Legend';
          }
        }
      }
    }

    if (!text) {
      const cell = node.closest('td:not([role="columnheader"]),[role="cell"],[role="gridcell"],[role="rowheader"]');
      if (cell) {
        const cells = cell.parentNode.querySelectorAll('td:not([role="columnheader"]),[role="cell"],[role="gridcell"],[role="rowheader"]');
        const cellIndex = Array.from(cells).indexOf(cell);
        if (cellIndex >= 0) {
          const table = node.closest('table,[role="table"]');
          if (table) {
            const headers = this.getTableHeaders(table);
            if (headers.length >= cellIndex) {
              text = headers[cellIndex]?.textContent?.trim();
              if (text) {
                source = 'Table Header';
              }
            }
          }
        }
      }
    }

    if (!text && node && node.hasAttribute('placeholder')) {
      text = node.getAttribute('placeholder')?.trim();
      if (text) {
        source = 'Placeholder';
      }
    }

    if (!text && node && node.name) {
      text = this.getFriendlyID(node.name);
      if (text) {
        source = 'Name';
      }
    }

    if (!text && node && node.id) {
      text = this.getFriendlyID(node.id);
      if (text) {
        source = 'ID';
      }
    }

    if (!text && !node.isContentEditable) {
      text = nodeText;
      if (text) {
        source = 'Inner Text';
      }
    }

    if (text && text.endsWith('*')) {
      text = text.slice(0, -1).trim();
    }

    if (!text && node.isContentEditable) {
      text = 'Unknown';
    }

    text = (text || nodeText || '').replace(/\s+/gm, ' ') || 'Unknown';
    if (text && text.length > 50) {
      text = text.slice(0, 50).trim();
    }

    return {
      text: text || 'Unknown',
      source
    };
  }

  /**
   *
   */
  getAllTableElements(el: any): Element[] {
    const tables: Element[] = [];
    const root = this.getFormElement(el) ?? document;
    const els = root.querySelectorAll('table, [role="table"], [recorder-shadow]');
    for (let i = 0, l = els.length; i < l; i++) {
      const _el = els[i];
      const _shadowEl = _el.shadowRoot;
      if (_shadowEl) {
        const sEls = _shadowEl.querySelectorAll('table, [role="table"]');
        if (sEls.length) {
          tables.push(...sEls);
        }
      } else {
        tables.push(_el);
      }
    }

    return tables;
  }

  /**
   * Get header columns for the specified table
   */
  getTableHeaders(table: any) {
    let headers: any;

    const headerId = table.getAttribute('aria-labelledby');
    if (headerId) {
      let headerEl = document.getElementById(headerId);
      if (headerEl) {
        if (headerEl.nodeName === 'THEAD' || this.getValidAttributeValue(headerEl, 'role') === 'rowgroup') {
          headerEl = headerEl.parentElement;
        }

        headers = this.getTableHeaders(headerEl);
        if (headers) {
          return headers;
        }
      }
    }

    const thead = table.querySelector('thead');
    if (thead) {
      headers = thead.querySelectorAll('th');
    } else {
      const group = table.querySelector('rowgroup');
      if (group) {
        headers = group.querySelectorAll('th:not([role="rowheader"]),[role="columnheader"]');
      } else {
        const row = table.querySelector('tr,[role="row"]');
        if (row) {
          headers = row.querySelectorAll('th:not([role="rowheader"]),[role="columnheader"]');
        }
      }
    }

    return headers;
  }

  /**
   * Gets all DOM ancestors of the specified element
   */
  getLineageFields(domEl: IDomElement, label?: string): IField[] {
    if (this.logging && this.logLevels.verbose) {
      console.group('getLineageFields');
    }

    const _el = domEl.node;
    if (!_el || _el.nodeType !== Node.ELEMENT_NODE || (_el.host && _el.nodeName !== 'A')) {
      return [];
    }

    const _role = this.getNodeRole(_el);

    let _val = '';
    if (_el.value && _el.value !== 'on') {
      _val = _el.value;
    } else if (_el.isContentEditable) {
      _val = _el.innerText;
    }

    if (this.logging && this.logLevels.verbose) {
      console.log('getLineageFields', _val, _el);
    }

    let _text = '';
    if (!_el.isContentEditable) {
      if (_el.nodeName === 'SELECT') {
        _text = this.getSelectedOptionText(_el);
      } else if (_role === 'listitem' || _role === 'treeitem' || _role === 'checklistitem') {
        _text = this.getDeepText(_el) || '';
      } else if (_role === 'table' || _el.nodeName === 'TABLE') {
        _text = _el.innerText;
      } else {
        _text = this.getElementText(_el, _role) || '';
      }
    }

    const _id = this.getElementID(_el);
    const attrs: IAttributes = {
      id: _id.value,
      id_source: _id.attribute,
      value: _val,
      text: _text,
      classes: _el.className,
      aria: this.getAriaLabel(_el),
      invalid: this.getValidAttributeValue(_el, 'aria-invalid'), // NOTE: invalid is not a property
      name: this.getValidPropertyValue(_el, 'name'),
      role: this.getValidPropertyValue(_el, 'role'),
      href: this.getValidPropertyValue(_el, 'href'),
      title: this.getValidPropertyValue(_el, 'title'),
      type: this.getValidPropertyValue(_el, 'type'),
      checked: this.getValidPropertyValue(_el, 'checked', true),
      disabled: this.getValidPropertyValue(_el, 'disabled', true),
      required: this.getValidPropertyValue(_el, 'required', true),
      readonly: this.getValidPropertyValue(_el, 'readOnly', true),
      pressed: this.getValidPropertyValue(_el, 'pressed', true),
      hidden: this.getValidPropertyValue(_el, 'hidden', true),
      expanded: this.getValidPropertyValue(_el, 'expanded', true),
      selected: this.getValidPropertyValue(_el, 'selected', true)
    };

    const l: ILabel = label
      ? { text: label, source: 'Label' }
      : this.getLabelText(_el, _role, _text || _val);
    const g = this.getGroupLabel(_el, l.text, _role, attrs);
    const _label = g && l.text && !ObserverCommon.compareValues(g, l.text)
      ? `${l.text} - ${g}`
      : l.text;

    const lastDef = this.getFieldDefinition(_el.nodeName, _role);
    const parents = domEl.path;
    const fields: IField[] = [];
    const cFields: IField[] = [];

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { domEl });

    if (parents.length) {
      this.populateLineage(parents, fields, cFields, lastDef);
    }

    const _rootEl = _el.getRootNode ? _el.getRootNode() : document;
    const _selectors = this.getSelectors(_el, parents, true);

    let _position: IPosition;
    if (_rootEl.host) {
      _position = this.getPositionByCss(_el, _rootEl, _selectors.css);
    } else {
      let lastField: IField | undefined;
      if (fields.length) {
        lastField = fields[fields.length - 1];
      } else if (cFields.length) {
        lastField = cFields[cFields.length - 1];
      }
      _position = this.getPositionByXpath(_el, l.source, lastField, _selectors.xpath);
    }

    const _field: IField = {
      fid: '',
      tag: _el.nodeName,
      label: _label,
      label_source: l.source,
      type: _role || (_label ? 'text' : ''),
      ordinal: _position.ordinal,
      maxOrdinal: _position.total,
      coordinates: this.getElementCoordinates(_el),
      attributes: attrs,
      selectors: _selectors
    };

    _field.fid = ObserverCommon.getHash(JSON.stringify(_field));
    _field.element = _el;
    _field.alert = this.getFieldAlerts(_position, _selectors, l, _field.type);
    _field.window = ObserverCommon.getWindowType();

    fields.push(_field);

    if (cFields.length) {
      cFields.push(_field);
    }

    if (this.logging && this.logLevels.verbose) {
      console.groupEnd();
    }

    return cFields.length ? cFields : fields;
  }

  /**
   * Iterate DOM ancestors and populate field arrays
   */
  populateLineage(parents: any[], fields: IField[], cFields: IField[], lastDef?: IFieldDefinition) {
    if (this.logging && this.logLevels.verbose) {
      console.group('populateLineage');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { parents, fields, cFields, lastDef });

    let _lastDef = lastDef;

    for (let idx = 0, len = parents.length; idx < len; idx++) {
      const p = parents[idx];

      if (p.host && p.nodeName !== 'A') {
        continue;
      }

      const o = this.getValidAttributeValue(p, 'aria-ownedby') || this.getValidAttributeValue(p, 'aria-flowto');
      const r = this.getNodeRole(p);
      const i = this.getValidAttributeValue(p, 'id');
      let owner: any;
      let ownerAlt: any;
      let p_field: IField | undefined;

      ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { p, o, r, d: ObserverCommon.isDisplayed(p) });

      if (i) {
        ownerAlt = document.querySelector(`[aria-owns="${i}"]`);
        if (ownerAlt && this.getNodeRole(ownerAlt) !== 'combobox') {
          ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { msg: 'has aria-owns' });

          owner = ownerAlt;
          const _parents = [owner, ...this.getParents(owner).parents];
          this.populateLineage(_parents, fields, cFields, _lastDef);
          return;
        }
      }

      if (r && ObserverCommon.valid_roles.includes(r) && ObserverCommon.isDisplayed(p)) {
        const def = this.getFieldDefinition(p.nodeName, r);
        if (def) {
          // A prior def was found
          // This is not the defs parent
          if (_lastDef && _lastDef.parent !== def.name) {
            continue;
          }

          // A prior def was not found
          // This def one is not clickable
          if (!_lastDef && !def.clickable) {
            continue;
          }

          _lastDef = def;
        }

        let v = '';
        if (p.value && p.value !== 'on') {
          v = p.value;
        } else if (p.isContentEditable) {
          v = p.innerText;
        }

        let t = '';
        if (!p.isContentEditable) {
          if (p.nodeName === 'SELECT') {
            t = this.getSelectedOptionText(p);
          } else if (r === 'listitem' || r === 'treeitem' || r === 'checklistitem') {
            t = this.getDeepText(p) || '';
          } else if (r === 'table' || p.nodeName === 'TABLE') {
            t = p.textContent;
          } else {
            t = this.getElementText(p, r) || '';
          }
        }

        const _id = this.getElementID(p);
        const attrs: IAttributes = {
          id: _id.value,
          id_source: _id.attribute,
          value: v,
          text: t,
          classes: p.className,
          aria: this.getAriaLabel(p),
          invalid: this.getValidAttributeValue(p, 'aria-invalid'), // NOTE: labelledby is not a property
          name: this.getValidPropertyValue(p, 'name'),
          role: this.getValidAttributeValue(p, 'role'), // NOTE: role is not a property
          href: this.getValidPropertyValue(p, 'href'),
          title: this.getValidPropertyValue(p, 'title'),
          type: this.getValidPropertyValue(p, 'type'),
          checked: this.getValidPropertyValue(p, 'checked', true),
          disabled: this.getValidPropertyValue(p, 'disabled', true),
          required: this.getValidPropertyValue(p, 'required', true),
          readonly: this.getValidPropertyValue(p, 'readonly', true),
          pressed: this.getValidPropertyValue(p, 'pressed', true),
          hidden: this.getValidPropertyValue(p, 'hidden', true),
          expanded: this.getValidPropertyValue(p, 'expanded', true),
          selected: this.getValidPropertyValue(p, 'selected', true)
        };

        const l = this.getLabelText(p, r, t || v);
        const g = this.getGroupLabel(p, l.text, r, attrs);
        const _label = g && l.text && !ObserverCommon.compareValues(g, l.text)
          ? `${l.text} - ${g}`
          : l.text;

        const _rootEl = p.getRootNode ? p.getRootNode() : document;
        const _parents = parents.slice(idx + 1);
        const _selectors = this.getSelectors(p, _parents, true);

        let _position: IPosition;
        if (_rootEl.host) {
          _position = this.getPositionByCss(p, _rootEl, _selectors.css);
        } else {
          let lastField: IField | undefined;
          if (fields.length) {
            lastField = fields[0];
          } else if (cFields.length) {
            lastField = cFields[0];
          }

          _position = this.getPositionByXpath(p, l.source, lastField, _selectors.xpath);
        }

        p_field = {
          fid: '',
          type: r || (l.text ? 'text' : ''),
          tag: p.nodeName,
          label: _label,
          label_source: l.source,
          ordinal: _position.ordinal,
          maxOrdinal: _position.total,
          coordinates: this.getElementCoordinates(p),
          attributes: attrs,
          selectors: _selectors
        };

        p_field.fid = ObserverCommon.getHash(JSON.stringify(p_field));
        p_field.element = p;
        p_field.alert = this.getFieldAlerts(_position, _selectors, l, p_field.type);
        p_field.window = ObserverCommon.getWindowType();

        if (def) {
          cFields.unshift(p_field);
        } else {
          fields.unshift(p_field);
        }
      }

      if (!owner && o) {
        owner = document.querySelector(`[id="${o}"], [ng-reflect-id="${o}"]`);
      }

      if (owner) {
        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { msg: 'has owner' });

        if (p_field !== undefined) {
          p_field.ownerForm = this.parseForm(owner);
        }

        const _parents = [owner, ...this.getParents(owner).parents];
        this.populateLineage(_parents, fields, cFields, _lastDef);
        return;
      }

      if (ownerAlt && p_field !== undefined) {
        p_field.ownerForm = this.parseForm(ownerAlt);
      }
    }

    if (this.logging && this.logLevels.verbose) {
      console.groupEnd();
    }
  }

  /**
   * Initializes functions to capture native browser dialogs.
   *
   * (e.g., alert, confirm, prompt).
   */
  initWindowDialogs() {
    this.windowDialog = {
      fid: '',
      label: 'Window',
      type: 'dialog',
      attributes: {},
      element: null,
      selectors: {}
    };
    this.windowDialog.fid = ObserverCommon.getHash(JSON.stringify(this.windowDialog));
    this.windowDialog.window = ObserverCommon.getWindowType();

    this.okDialogButton = {
      fid: '',
      label: 'OK',
      type: 'dialogbutton',
      attributes: {},
      element: null,
      selectors: {}
    };
    this.okDialogButton.fid = ObserverCommon.getHash(JSON.stringify(this.okDialogButton));
    this.okDialogButton.window = ObserverCommon.getWindowType();

    this.cancelDialogButton = {
      fid: '',
      label: 'Cancel',
      type: 'dialogbutton',
      attributes: {},
      element: null,
      selectors: {}
    };
    this.cancelDialogButton.fid = ObserverCommon.getHash(JSON.stringify(this.cancelDialogButton));
    this.cancelDialogButton.window = ObserverCommon.getWindowType();


    (function (method, sr) {
      window.alert = function (message) {
        sr.alertOpened(message);
        method.call(window, message);
        sr.alertClosed();
      };
    })(window.alert, this);

    (function (method, sr) {
      window.confirm = function (message) {
        sr.confirmOpened(message);
        const result = method.call(window, message);
        sr.confirmClosed(result);
        return result;
      };
    })(window.confirm, this);

    (function (method, sr) {
      window.prompt = function (message, defaultText) {
        sr.promptOpened(message, defaultText);
        const result = method.call(window, message, defaultText);
        sr.promptClosed(result, message);
        return result;
      };
    })(window.prompt, this);
  }

  /**
   *
   */
  alertOpened(message: string) {
    this.addDialogEvent(message);
  }

  /**
   *
   */
  confirmOpened(message?: string) {
    this.addDialogEvent(message);
  }

  /**
   *
   */
  promptOpened(message?: string, _default?: string) {
    this.addDialogEvent(message);
  }

  /**
   *
   */
  addDialogEvent(message?: string) {
    if (!this.windowDialog) {
      return;
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: this.last_event
        ? this.last_event.form
        : this.parseForm(),
      event: 'Validate',
      trusted: false,
      fields: [this.windowDialog],
      fid: this.windowDialog.fid,
      validate: {
        name: 'value',
        value: message,
        condition: ECondition.equals,
        type: 'Validate'
      }
    };

    this.pushEvent(new_event, this.windowDialog);
  }

  /**
   *
   */
  alertClosed() {
    if (!this.okDialogButton) {
      return;
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: this.last_event
        ? this.last_event.form
        : this.parseForm(),
      event: 'Click',
      trusted: true,
      fields: [this.okDialogButton],
      fid: this.okDialogButton.fid
    };

    this.pushEvent(new_event, this.okDialogButton);
  }

  /**
   *
   */
  confirmClosed(result: boolean) {
    const field = result
      ? this.okDialogButton
      : this.cancelDialogButton;

    if (!field) {
      return;
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: this.last_event
        ? this.last_event.form
        : this.parseForm(),
      event: 'Click',
      trusted: true,
      fields: [field],
      fid: field.fid
    };

    this.pushEvent(new_event, field);
  }

  /**
   *
   */
  promptClosed(result: string | null, label: string | undefined) {
    const btn = result === null
      ? this.cancelDialogButton
      : this.okDialogButton;

    if (!btn) {
      return;
    }

    if(result !== null){
      const promptDialogInput: IField = {
        fid: '',
        label: label || 'Unknown',
        type: 'dialoginput',
        attributes: {
          value: result
        },
        element: null,
        selectors: {}
      };
      promptDialogInput.fid = ObserverCommon.getHash(JSON.stringify(promptDialogInput));
      promptDialogInput.window = ObserverCommon.getWindowType();

      this.pushEvent({
        uid: Math.random().toString(36).slice(2),
        time: Date.now(),
        url: window.location.href,
        form: this.last_event
          ? this.last_event.form
          : this.parseForm(),
        event: 'Set',
        trusted: true,
        fields: [promptDialogInput],
        fid: promptDialogInput.fid
      }, promptDialogInput);
    }

    this.pushEvent({
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: this.last_event
        ? this.last_event.form
        : this.parseForm(),
      event: 'Click',
      trusted: true,
      fields: [btn],
      fid: btn.fid
    }, btn);
  }

  /**
   * Build validation event
   */
  validate(el: any, validation: IValidation) {
    if (!el || this.ignoreEvent(el)) {
      return 'Element not provided';
    }

    const _dropdown = el.closest('p-dropdown');
    if (_dropdown) {
      el = _dropdown;
    }

    const domEl = this.getElementPath(el);
    const fields = this.getLineageFields(domEl);
    if (!fields.length) {
      return;
    }

    const target = this.getTargetField(fields, 'Validate');
    if (!target || !target.field) {
      return 'No valid element found';
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: target.form || this.parseForm(target.element),
      event: validation.type,
      trusted: validation.type !== 'Validate',
      fields,
      fid: target.field.fid,
      validate: validation,
      keys: {
        ctrl: this.ctrl_key,
        alt: this.alt_key,
        shift: this.shift_key,
      },
    };

    this.pushEvent(new_event, target.field, target.element);
  }

  /**
   *
   */
  formEvent(el: HTMLElement) {
    const event_form = this.getEventForm(el);
    if (!event_form) {
      return;
    }

    const new_event: IEvent = {
      uid: Math.random().toString(36).slice(2),
      time: Date.now(),
      url: window.location.href,
      form: event_form,
      event: 'Form',
      trusted: false,
      fields: [],
      fid: ''
    };

    this.pushEvent(new_event, undefined, el);
  }

  /**
   * Creates an event form for the specified element
   */
  getEventForm(el: HTMLElement): IEventForm | undefined {
    let eid: string | undefined;
    let name: string | undefined;
    let source: string | undefined;

    if (el[this.id_attr]) {
      eid = el[this.id_attr];
    } else if (el.hasAttribute(this.id_attr)) {
      eid = this.getValidAttributeValue(el, this.id_attr);
    }

    if (el[this.name_attr]) {
      name = el[this.name_attr];
    } else if (el.hasAttribute(this.name_attr)) {
      name = this.getValidAttributeValue(el, this.name_attr) || el.textContent || undefined;
    }

    if (name) {
      name = name.replace(/\s+/gm, ' ');
      source = 'Attribute';
    }

    if (eid || name) {
      return {
        name: name || 'Unknown',
        id: eid ?? '',
        alert: name ? '' : 'Form Not Found',
        source
      };
    }
  }

  /**
   *
   */
  getLastStep(events: IEvent[]) {
    const n = events.length;
    if (n === 0) {
      return null;
    }

    return events[n - 1];
  }

  /**
   *
   */
  parseExternalID(id: string): IExternalID {
    const regex = /(\[(\w+)\]){0,1}(.+)/gi;
    let m: any;
    let eid: IExternalID = {};

    while ((m = regex.exec(id)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === regex.lastIndex) {
        regex.lastIndex++;
      }

      eid = {
        ExternalID: m,
        NameSpace: m[2]
      };
    }

    return eid || {};
  }

  /**
   *
   */
  pushEvent(event: IEvent, field?: IField, el?: any) {
    ObserverCommon.consoleData(this.logging, BuildVersion, 'pushEvent', EAlertType.verbose, this.logLevels, { event, field, el });

    if (!this.observerEvent) {
      return;
    }

    if ((field?.type === 'row' || field?.type === 'cell') && event.event !== 'Assert') {
      const tbl = el.closest('table');
      if (tbl && tbl.getAttribute('role') === 'presentation') {
        if (ObserverCommon.isDuplicateEvent(event, this.last_event)) {
          if (event.event === 'Click') {
            event.event = 'DoubleClick';
          } else {
            this.last_event = event;
            return;
          }
        } else {
          this.last_event = event;
          return;
        }
      }
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'Pushing Event...', EAlertType.verbose, this.logLevels, { event, lastEvent: this.last_event });

    this.last_event = event;

    const evt = this.customEvent(this.observerEvent, { detail: event, bubbles: true });
    document.dispatchEvent(evt);

    ObserverCommon.consoleData(this.logging, BuildVersion, 'Event Pushed', EAlertType.verbose, this.logLevels, { event, lastEvent: this.last_event });
  }

  /**
   *
   */
  customEvent(eventName: string, params: IEventParams) {
    if (typeof window.CustomEvent !== 'function') {
      const CustomEvent = (event: string, params: IEventParams) => {
        params = params || {
          bubbles: false,
          cancelable: false,
          detail: undefined
        };
        const evt = document.createEvent('CustomEvent');
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
        return evt;
      };

      CustomEvent.prototype = window.Event.prototype;

      (window as any).CustomEvent = CustomEvent;
    }

    return new window.CustomEvent(eventName, params);
  }

  /**
   * Get the target element of the specified event
   */
  getEventTarget(e: any): IDomElement {
    let path: any[];
    let target: any;

    if (e.composedPath) {
      path = e.composedPath();
      target = path.shift();

      let idx = path.indexOf(document.body);
      if (idx === 0)
        path = [];
      else if (idx === -1) {
        idx = path.indexOf(document);
        if (idx > 0) {
          path.splice(idx);
        } else {
          path = [];
        }
      } else {
        path.splice(idx);
      }

      return {
        path,
        node: target
      };
    }

    const _e = e || window.event;
    target = _e.target || _e.srcElement;

    return this.getElementPath(target);
  }

  // keep this separate for validateElement
  getElementPath(el: any): IDomElement {
    let target = el;
    const path: any[] = [];

    if (target?.nodeType === Node.TEXT_NODE) {
      target = target.parentNode;
    }

    while (target && target != document && target != document.body && target != window) {
      path.push(target);
      if (target.parentNode == document.body)
        break;
      target = target.parentNode;
    }

    return {
      path,
      node: target
    };
  }

  /**
   *
   */
  getTargetField(fields: IField[], action?: string): ITarget | undefined {
    if (this.logging && this.logLevels.verbose) {
      console.group('getTargetField');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.info, this.logLevels, { fields, action });

    let n = fields.length;
    let _field: IField | undefined;
    let _parent: IField | undefined;
    let _def: IFieldDefinition | undefined;
    let _action = action;

    while (n--) {
      const f = fields[n];

      if (!f.type) {
        continue;
      }

      if (!_def) {
        _def = this.getFieldDefinition(f.tag, f.type);

        if (_def || (!_field && !_parent && (_action === 'Validate' || (f.type && ObserverCommon.change_roles.includes(f.type))))) {
          _field = f;
        }

        if (_field || _parent || _def) {
          ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { _field, _parent, _def });
        }

        if (_field && !_parent && !_def) {
          const containers = _field.type
            ? this.field_containers[_field.type]
            : null;
          const validContainer = f.type && containers && containers.length && containers.includes(f.type);
          if (validContainer) {
            _parent = f;
            _parent.selectors.xpath2 = _field.selectors.xpath;
            _parent.selectors.xpath2_source = _field.selectors.xpath_source;
            _parent.selectors.css2 = _field.selectors.css;
            _parent.attributes.text = _field.attributes.text;
            _parent.attributes.value = _field.attributes.value;
          }
        }

        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { _field, _parent, _def });
      } else if (_def.action === 'Select') {
        const pDef = this.getFieldDefinition(_def.parent);
        if (pDef && _field) {
          _def = pDef;
          _parent = f;
          _parent.selectors.xpath2 = _field.selectors.xpath;
          _parent.selectors.xpath2_source = _field.selectors.xpath_source;
          _parent.selectors.css2 = _field.selectors.css;
          _parent.attributes.text = _field.attributes.text;
          _parent.attributes.value = _field.attributes.value;
          if (_action !== 'Validate') {
            _action = _def.action;
          }
          break;
        }
      }
    }

    const targetField = _parent || _field;

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { targetField });

    if (this.logging && this.logLevels.verbose) {
      console.groupEnd();
    }

    if (targetField) {
      return {
        field: targetField,
        definition: _def,
        action: _action || _def?.action,
        element: targetField.element,
        form: targetField.ownerForm
      } as ITarget;
    }
  }

  /**
   *
   */
  isValidationField(field: IField): boolean {
    if (field.type && (field.type === 'label' || ObserverCommon.click_roles.includes(field.type))) {
      return true;
    }

    return false;
  }

  /**
   *
   */
  getChangeEvent(el: any, fields: IField[]): TEventType {
    ObserverCommon.consoleData(this.logging, BuildVersion, 'getChangeEvent', EAlertType.info, this.logLevels, { el });

    let _event: TEventType;
    const _role: string = this.getNodeRole(el);

    switch (_role) {
      case 'select':
      case 'dropdown':
      case 'dropdownlist':
      case 'option':
        return 'Select';
      case 'textarea':
        return 'Set';
      case 'check':
      case 'checkbox':
        return this.getCheckEvent(el);
      case 'radio':
      case 'radiobutton':
        return 'Click';
      case 'input':
      case 'inputbox':
      case 'combobox':
      case 'listbox':
      case 'textbox':
      case 'spinbutton': {
        const _type: string = el.type;
        if (_type === 'checkbox') {
          return this.getCheckEvent(el);
        }
        if (_type === 'radio') {
          return 'Click';
        }
        return 'Set';
      }
      case 'checklist':
        return this.getSelectEvent(fields);
      default:
        _event = 'Other';
        ObserverCommon.consoleData(this.logging, BuildVersion, 'getChangeEvent', EAlertType.verbose, this.logLevels, { msg: 'Unknown Change Event', _role });
        break;
    }

    return _event;
  }

  /**
   * Determines the true target element
   * e.g., clicking list item gets the list element
   * Mostly for dropdowns
   */
  getClickTargetField(fields: IField[], action?: string): ITarget | undefined {
    if (this.logging && this.logLevels.verbose) {
      console.group('getClickTargetField');
    }

    ObserverCommon.consoleData(this.logging, BuildVersion, 'getClickTargetField', EAlertType.info, this.logLevels, { fields, action });

    let n = fields.length;
    let _field: IField | undefined;
    let _parent: IField | undefined;
    let _def: IFieldDefinition | undefined;
    let _action = action;

    while (n--) {
      const f = fields[n];

      if (!f.type) {
        continue;
      }

      if (!_def) {
        const def = this.getFieldDefinition(f.tag, f.type);

        if (def?.clickable) {
          _def = def;
        }

        if (_def || (!_field && !_parent && ObserverCommon.click_roles.includes(f.type))) {
          _field = f;
        }

        ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { _field, _parent, _def });

        if (_field && !_parent && !_def) {
          const containers = _field.type
            ? this.field_containers[_field.type]
            : null;
          const validContainer = f.type && containers && containers.length && containers.includes(f.type);
          if (validContainer) {
            _parent = f;
            _parent.selectors.xpath2 = _field.selectors.xpath;
            _parent.selectors.xpath2_source = _field.selectors.xpath_source;
            _parent.selectors.css2 = _field.selectors.css;
            _parent.attributes.text = _field.attributes.text;
            _parent.attributes.value = _field.attributes.value;
            _action = f.type == 'checklist'
              ? 'Check'
              : 'Select';
          }
        }
      } else if (_def.action === 'Select') {
        const pDef = this.getFieldDefinition(_def.parent);
        if (_field && pDef) {
          _def = pDef;
          _parent = f;
          _parent.selectors.xpath2 = _field.selectors.xpath;
          _parent.selectors.xpath2_source = _field.selectors.xpath_source;
          _parent.selectors.css2 = _field.selectors.css;
          _parent.attributes.text = _field.attributes.text;
          _parent.attributes.value = _field.attributes.value;
          _action = _def.action;
          break;
        }
      }
    }

    const targetField = _parent || _field;

    ObserverCommon.consoleData(this.logging, BuildVersion, '', EAlertType.verbose, this.logLevels, { targetField });

    if (this.logging && this.logLevels.verbose) {
      console.groupEnd();
    }

    if (targetField?.type === 'checklist') {
      _action = this.getSelectEvent(fields, true);
    } else if (targetField?.type === 'checkbox') {
      _action = this.getCheckEvent(targetField?.element);
    }

    if (targetField) {
      return {
        field: targetField,
        definition: _def,
        action: _action || _def?.action || 'Other',
        element: targetField.element,
        form: targetField.ownerForm
      } as ITarget;
    }
  }

  /**
   * Return check or uncheck depending on the current state
   */
  getCheckEvent(el: any): TEventType {
    const hasAriaChecked = el.hasAttribute('aria-checked') || !ObserverCommon.isNullOrUndefined(el.ariaChecked);
    if (hasAriaChecked) {
      return this.getValidPropertyValue(el, 'checked', true) === 'true'
        ? 'Uncheck'
        : 'Check';
    }

    const hasChecked = el.hasAttribute('checked') || !ObserverCommon.isNullOrUndefined(el.checked);
    if (hasChecked) {
      return this.getValidPropertyValue(el, 'checked', true) === 'true'
        ? 'Check'
        : 'Uncheck';
    }

    return 'Click';
  }

  /**
   * Return select or deselect depending on the current state
   */
  getSelectEvent(fields: IField[], negate?: boolean): TEventType {
    const field = fields.find((f) => f.type === 'checklistitem' || f.type === 'listitem' || f.type === 'option');
    const el = field?.element;
    if (!el) {
      return 'Select';
    }

    const selected = this.getValidPropertyValue(el, 'selected', true);
    const checked = this.getValidPropertyValue(el, 'checked', true);
    if (!selected && !checked) {
      const check = el.querySelector(':scope > input[type="checkbox"]');
      if (check) {
        if (negate) {
          return check.checked
            ? 'Select'
            : 'Deselect';
        }

        return check.checked
          ? 'Deselect'
          : 'Select';
      }
    }

    if (negate) {
      return selected === 'true' || checked == 'true'
        ? 'Deselect'
        : 'Select';
    }

    return selected === 'true' || checked == 'true'
      ? 'Select'
      : 'Deselect';
  }
}
